// src/pages/AddSystemPage.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth, storage } from "../firebase/firebase-config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Container, Row, Col, Form, Button, Card, Alert, Spinner } from "react-bootstrap";
import { FaArrowLeft, FaSave, FaStar, FaTimes, FaUpload, FaGraduationCap, FaChalkboardTeacher, FaLink, FaFileAlt } from "react-icons/fa";

function AddSystemPage() {
  const [formData, setFormData] = useState({
    title: "",               // Course name
    courseNumber: "",        // Course number
    yearTaken: new Date().getFullYear(),  // Default to current year
    professor: "",           // Professor/instructor
    description: "",         // Course description
    imageUrl: "",            // Course cover image URL
    courseWebsite: "",       // Course website URL
    courseNotesUrl: "",      // Course notes URL
    rating: 0,               // Course rating
    tags: [],                // Course tags/categories
    status: "upcoming",      // Default to "Upcoming" (upcoming, inprogress, completed)
  });
  const [imageFile, setImageFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [pdfFileName, setPdfFileName] = useState("");
  const [uploadProgress, setUploadProgress] = useState({ image: 0, pdf: 0 });
  const [isUploading, setIsUploading] = useState({ image: false, pdf: false });
  const [tagInput, setTagInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/pdf') {
        setPdfFile(file);
        setPdfFileName(file.name);
      } else {
        setError("Please upload a PDF file");
        setPdfFile(null);
        setPdfFileName("");
      }
    }
  };

  const handleRatingChange = (rating) => {
    setFormData({
      ...formData,
      rating
    });
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim() !== "" && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const uploadImageToFirebase = async (file) => {
    if (!file) return null;
    
    try {
      setIsUploading(prev => ({ ...prev, image: true }));
      setUploadProgress(prev => ({ ...prev, image: 0 }));
      
      // Create a unique filename with timestamp and original file extension
      const fileExtension = file.name.split('.').pop();
      const fileName = `book_images/${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExtension}`;
      
      // Create a reference to the storage location
      const storageRef = ref(storage, fileName);
      
      // Upload the file
      console.log('Starting upload of image file:', file.name);
      const uploadTask = await uploadBytes(storageRef, file);
      setUploadProgress(prev => ({ ...prev, image: 50 }));
      
      // Get the download URL
      console.log('Upload complete, getting download URL');
      const downloadURL = await getDownloadURL(uploadTask.ref);
      console.log('Download URL:', downloadURL);
      setUploadProgress(prev => ({ ...prev, image: 100 }));
      
      return downloadURL;
    } catch (error) {
      console.error("Error uploading image:", error);
      console.error("Error details:", error.code, error.message);
      throw new Error(`Failed to upload image: ${error.message}`);
    } finally {
      setIsUploading(prev => ({ ...prev, image: false }));
    }
  };

  const uploadPdfToFirebase = async (file) => {
    if (!file) return null;
    
    try {
      setIsUploading(prev => ({ ...prev, pdf: true }));
      setUploadProgress(prev => ({ ...prev, pdf: 0 }));
      
      // Create a unique filename with timestamp and book title
      const safeTitle = formData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'untitled';
      const fileName = `book_pdfs/${safeTitle}_${Date.now()}.pdf`;
      
      // Create a reference to the storage location
      const storageRef = ref(storage, fileName);
      
      // Upload the file
      console.log('Starting upload of PDF file:', file.name);
      const uploadTask = await uploadBytes(storageRef, file);
      setUploadProgress(prev => ({ ...prev, pdf: 50 }));
      
      // Get the download URL
      console.log('PDF upload complete, getting download URL');
      const downloadURL = await getDownloadURL(uploadTask.ref);
      console.log('PDF Download URL:', downloadURL);
      setUploadProgress(prev => ({ ...prev, pdf: 100 }));
      
      return downloadURL;
    } catch (error) {
      console.error("Error uploading PDF:", error);
      console.error("Error details:", error.code, error.message);
      throw new Error(`Failed to upload PDF: ${error.message}`);
    } finally {
      setIsUploading(prev => ({ ...prev, pdf: false }));
    }
  };

  // Create course data object with backward compatibility fields
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Upload image if selected
      let imageUrl = formData.imageUrl;
      
      if (imageFile) {
        imageUrl = await uploadImageToFirebase(imageFile);
      }
      
      // Create course data object with backward compatibility
      const courseData = {
        ...formData,
        imageUrl,
        rating: parseInt(formData.rating) || 0,
        yearTaken: parseInt(formData.yearTaken) || new Date().getFullYear(),
        readingStatus: formData.status, // Map to existing field for backward compatibility
        websiteLink: formData.courseWebsite, // For backward compatibility
        readNowLink: formData.courseNotesUrl, // For backward compatibility
        author: formData.professor, // Map professor to author for backward compatibility
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        uid: auth.currentUser.uid
      };
      
      // Add document to Firestore
      await addDoc(collection(db, "books"), courseData);
      
      console.log("Course added successfully");
      navigate("/"); // Navigate back to home page
    } catch (err) {
      console.error("Error adding course:", err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Rating component
  const RatingSelector = ({ value, onChange }) => {
    return (
      <div className="d-flex align-items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={`star-rating ${star <= value ? 'text-warning' : 'text-muted'}`}
            style={{ cursor: 'pointer', fontSize: '1.5rem', marginRight: '0.25rem' }}
            onClick={() => onChange(star)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <main className="main-content" style={{ marginLeft: 0 }}>
        <Container className="py-4">
          <Row className="justify-content-center">
            <Col md={10} lg={8}>
              <div className="mb-4">
                <Button 
                  variant="link" 
                  className="p-0 text-decoration-none" 
                  onClick={() => navigate("/")}
                >
                  <FaArrowLeft className="me-2" />
                  Back to Dashboard
                </Button>
              </div>

              <Card className="shadow-sm border-0">
                <Card.Header className="bg-primary text-white">
                  <h4 className="mb-0">Add New Course</h4>
                </Card.Header>
                <Card.Body>
                  {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Course Title</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="Enter course title"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Professor</Form.Label>
                      <Form.Control
                        type="text"
                        name="professor"
                        value={formData.professor}
                        onChange={handleChange}
                        placeholder="Enter professor name"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Course Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="courseNumber"
                        value={formData.courseNumber}
                        onChange={handleChange}
                        placeholder="Enter course number"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Year Taken</Form.Label>
                      <Form.Control
                        type="number"
                        name="yearTaken"
                        value={formData.yearTaken}
                        onChange={handleChange}
                        placeholder="YYYY"
                      />
                    </Form.Group>

                    {/* Course Status Field */}
                    <Form.Group className="mb-4">
                      <Form.Label>Course Status</Form.Label>
                      <div className="d-flex flex-wrap gap-3">
                        <Form.Check
                          type="radio"
                          id="status-upcoming"
                          name="status"
                          value="upcoming"
                          checked={formData.status === "upcoming"}
                          onChange={handleChange}
                          label="Upcoming"
                          className="me-3"
                        />
                        <Form.Check
                          type="radio"
                          id="status-inprogress"
                          name="status"
                          value="inprogress"
                          checked={formData.status === "inprogress"}
                          onChange={handleChange}
                          label="In Progress"
                          className="me-3"
                        />
                        <Form.Check
                          type="radio"
                          id="status-completed"
                          name="status"
                          value="completed"
                          checked={formData.status === "completed"}
                          onChange={handleChange}
                          label="Completed"
                        />
                      </div>
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Upload Cover Image</Form.Label>
                          <div className="d-flex flex-column">
                            <div className="mb-2">
                              <input
                                type="file"
                                id="imageUpload"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="d-none"
                              />
                              <label htmlFor="imageUpload" className="btn btn-outline-primary mb-2 w-100">
                                <FaUpload className="me-2" /> Choose Image
                              </label>
                            </div>
                            
                            {imagePreview && (
                              <div className="image-preview mb-2 border rounded p-2 text-center">
                                <img 
                                  src={imagePreview} 
                                  alt="Preview" 
                                  style={{ maxHeight: '150px', maxWidth: '100%' }}
                                />
                              </div>
                            )}
                            
                            {isUploading.image && (
                              <div className="progress mb-2">
                                <div 
                                  className="progress-bar" 
                                  role="progressbar" 
                                  style={{ width: `${uploadProgress.image}%` }}
                                  aria-valuenow={uploadProgress.image} 
                                  aria-valuemin="0" 
                                  aria-valuemax="100"
                                >
                                  {uploadProgress.image}%
                                </div>
                              </div>
                            )}
                            
                            <div className="d-flex align-items-center">
                              <div className="small text-muted flex-grow-1">
                                {imageFile ? `Selected: ${imageFile.name}` : 'No file chosen'}
                              </div>
                              {imageFile && (
                                <Button 
                                  variant="link" 
                                  size="sm" 
                                  className="text-danger p-0"
                                  onClick={() => {
                                    setImageFile(null);
                                    setImagePreview(null);
                                  }}
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                          <Form.Text className="text-muted">
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Course Website</Form.Label>
                          <Form.Control
                            type="url"
                            name="courseWebsite"
                            value={formData.courseWebsite}
                            onChange={handleChange}
                            placeholder="https://example.com/course-website"
                          />
                          <Form.Text className="text-muted">
                            Link to the course's official website
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Upload Course Notes PDF</Form.Label>
                          <div className="d-flex flex-column">
                            <div className="mb-2">
                              <input
                                type="file"
                                id="pdfUpload"
                                accept="application/pdf"
                                onChange={handlePdfChange}
                                className="d-none"
                              />
                              <label htmlFor="pdfUpload" className="btn btn-outline-secondary mb-2 w-100">
                                <FaFileAlt className="me-2" /> Choose PDF
                              </label>
                            </div>
                            
                            {isUploading.pdf && (
                              <div className="progress mb-2">
                                <div 
                                  className="progress-bar" 
                                  role="progressbar" 
                                  style={{ width: `${uploadProgress.pdf}%` }}
                                  aria-valuenow={uploadProgress.pdf} 
                                  aria-valuemin="0" 
                                  aria-valuemax="100"
                                >
                                  {uploadProgress.pdf}%
                                </div>
                              </div>
                            )}
                            
                            <div className="d-flex align-items-center">
                              <div className="small text-muted flex-grow-1">
                                {pdfFileName ? `Selected: ${pdfFileName}` : 'No file chosen'}
                              </div>
                              {pdfFile && (
                                <Button 
                                  variant="link" 
                                  size="sm" 
                                  className="text-danger p-0"
                                  onClick={() => {
                                    setPdfFile(null);
                                    setPdfFileName("");
                                  }}
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                            <Form.Text className="text-muted mt-2">
                              Upload a PDF version of the course notes. Students will be able to access it by clicking on the link.
                            </Form.Text>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Enter course description"
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Rating</Form.Label>
                      <div>
                        <RatingSelector 
                          value={formData.rating} 
                          onChange={handleRatingChange} 
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Tags</Form.Label>
                      <div className="d-flex mb-2">
                        <Form.Control
                          type="text"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          placeholder="Add a tag"
                          className="me-2"
                        />
                        <Button 
                          variant="outline-secondary" 
                          onClick={handleAddTag}
                        >
                          Add
                        </Button>
                      </div>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {formData.tags.map((tag, index) => (
                          <div 
                            key={index} 
                            className="badge bg-light text-dark d-flex align-items-center p-2"
                          >
                            {tag}
                            <FaTimes 
                              className="ms-2 cursor-pointer" 
                              onClick={() => handleRemoveTag(tag)}
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                        ))}
                      </div>
                    </Form.Group>

                    <div className="d-grid gap-2 mt-4">
                      <Button 
                        variant="primary" 
                        type="submit" 
                        disabled={isSubmitting || isUploading.image || isUploading.pdf}
                        className="d-flex align-items-center justify-content-center"
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner size="sm" animation="border" role="status" />
                            <span className="ms-2">Adding...</span>
                          </>
                        ) : (
                          <>
                            <FaSave className="me-2" /> Add Course
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>
    </div>
  );
}

export default AddSystemPage;